import React, { useEffect, useState } from "react";
import db from "../../../../constants/database";

interface Testimonial {
  id: number;
  rating: number;
  text: string;
  author: {
    name: string;
    position: string;
  };
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    rating: 5,
    text: "This program was invaluable for onboarding our CFO. It gave us peace of mind knowing integration and performance were actively managed.",
    author: {
      name: "Lagos-based Client",
      position: "Mining Sector",
    },
  },
  {
    id: 2,
    rating: 4,
    text: "The structured check-ins helped us address small issues early. It saved us from what could have been a costly misalignment.",
    author: {
      name: "Executive Placement",
      position: "Telecoms",
    },
  },
  {
    id: 3,
    rating: 5,
    text: "Implementation was seamless and the ROI was immediate. Our candidates love the smooth recruiting experience, and our team is more productive than ever.",
    author: {
      name: "Lagos-based Client",
      position: "Recruitment Agency",
    },
  },
];

const Testimonials: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length,
    );
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, []);

  const StarRating = ({ rating }: { rating: number }) => (
    <div className="flex gap-1 mb-4">
      {[...Array(5)].map((_, i) => (
        <svg
          key={i}
          className={`w-4 h-4 ${i < rating ? "text-[#FFD609]" : "text-[gray]"}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );

  return (
    <div className="font-abel bg-[#fff] py-10 md:py-20">
      <div className="max-w-[1300px] mx-auto px-3 w-full h-full">
        <div className="text-center max-w-[850px] mx-auto pb-10">
          <h2 className="text-[24px] md:text-[50px] leading-[109.9%] text-[#222222]">
            What Clients Are Saying
          </h2>
          <p className="text-[16px] md:text-[20px] text-[#4E4E4E] pt-3">
            Real results from organizations that invested in executive success
          </p>
        </div>
        {/* Testimonials Carousel */}
        <div className="relative">
          <div className="flex overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="w-full flex-shrink-0 px-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {/* Render current testimonial in center, with partial views of adjacent ones */}
                    {[-1, 0, 1].map((offset) => {
                      const index =
                        (currentSlide + offset + testimonials.length) %
                        testimonials.length;
                      const isCenter = offset === 0;
                      return (
                        <div
                          key={`${testimonial.id}-${offset}`}
                          className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[white]/20 shadow-xl transition-all duration-300 border-l-[4px] border-[#0B60D6] ${
                            isCenter
                              ? "scale-98 bg-white/15"
                              : "md:block md:scale-95 md:opacity-70 hidden"
                          }`}
                        >
                          <StarRating rating={testimonials[index].rating} />
                          <p className="text-gray-100 mb-6 leading-relaxed">
                            "{testimonials[index].text}"
                          </p>
                          <div className="flex items-center gap-3">
                            <div>
                              <h4 className="font-semibold text-[black]">
                                {testimonials[index].author.name}
                              </h4>
                              <p className="text-sm text-[#0035AA]">
                                {testimonials[index].author.position}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 rounded-full shadow-lg transition-colors"
          >
            <img
              src={db.left}
              alt="25th and Staffing"
              className="w-[60px] h-[50px]"
            />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 rounded-full shadow-lg transition-colors"
          >
            <img
              src={db.right}
              alt="25th and Staffing"
              className="w-[60px] h-[50px]"
            />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide ? "bg-blue-400" : "bg-gray-600"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
